import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';

import Icon from '@99designs/react-components/components/Icon';
import IconButton from '@99designs/react-components/components/IconButton';
import FileSize from '@99designs/react-components/components/FileSize';
import RelativeTime from '@99designs/react-components/components/RelativeTime';
import Textarea from '@99designs/react-components/components/Textarea';

import FixedRatioImage from 'contest/components/fixed-ratio-image';
import Mediabox from 'contest/components/mediabox';

const fileShape = PropTypes.shape({
    name: PropTypes.string,
    size: PropTypes.number,
    thumb: PropTypes.string,
    time: PropTypes.number,
    shared: PropTypes.boolean,
    url: PropTypes.string,
});

class FileContainer extends Component {
    render() {
        return (
            <div>
                <Mediabox meta={this.renderMeta()} variations={['wide-meta']}>
                    {this.renderThumbnail()}
                    {this.props.overlay}
                </Mediabox>
            </div>
        );
    }

    renderThumbnail() {
        const file = this.props.file;
        if (file.thumb) {
            return <FixedRatioImage src={file.thumb} alt={file.name} size="cover" />;
        } else {
            const icon = file.missingFileMetadata ? 'question-mark-2' : 'document-1';
            return (
                <div className="brief-file-overlay-square-wrapper">
                    <div className="brief-file-overlay">
                        <div className="brief-file-overlay__content">
                            <div className="brief-file-overlay__icon">
                                <Icon variations={[icon, '2x']} />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    renderMeta() {
        const { file, comment } = this.props;
        if (file.missingFileMetadata) {
            return <FormattedHTMLMessage id="brief_file_missing_metadata" />;
        } else {
            return (
                <div className="brief-file-meta">
                    <div className="brief-file-meta__action">{this.props.actionButton}</div>
                    <div className="brief-file-meta__label">{this.renderFilename(file)}</div>
                    {this.renderSubLabel(file)}
                    <div className="brief-file-meta__comment">{comment}</div>
                </div>
            );
        }
    }

    renderFilename(file) {
        return file.shared ? <FormattedMessage id="design_from_99designs" /> : file.name;
    }

    renderSubLabel(file) {
        if (file.shared) return;

        return (
            <div className="brief-file-meta__sub-label">
                <div className="brief-file-meta__time">
                    <RelativeTime date={file.time * 1000} />
                </div>
                <div className="brief-file-meta__size">
                    <FileSize value={file.size} />
                </div>
            </div>
        );
    }
}
FileContainer.propTypes = {
    comment: PropTypes.element.isRequired,
    actionButton: PropTypes.element.isRequired,
    file: fileShape.isRequired,
    overlay: PropTypes.element,
};
FileContainer.defaultProps = {
    overlay: null,
};

export class EditFile extends React.Component {
    constructor(props) {
        super(props);

        this.onChange = this.onChange.bind(this);
        this.retry = this.retry.bind(this);
    }

    onChange(event) {
        this.props.onChange(event.target.value);
    }

    render() {
        return (
            <FileContainer
                overlay={this.renderOverlay()}
                comment={this.renderComment()}
                actionButton={this.renderActionButton()}
                file={this.props.file}
            />
        );
    }

    retry() {
        this.props.retryUpload(this.props.file.uploadid);
    }

    renderOverlay() {
        if (this.props.error) {
            return (
                <div className="brief-file-overlay brief-file-overlay--shaded">
                    <div className="brief-file-overlay__content">
                        <div>
                            <FormattedMessage id="brief_upload_error" />
                            <br />
                            <button
                                className="button button--link link link--rising-sun"
                                onClick={this.retry}
                            >
                                <FormattedMessage id="save_indicator_retry_save" />
                            </button>
                        </div>
                    </div>
                </div>
            );
        } else if (typeof this.props.progress == 'number') {
            return (
                <div className="brief-file-overlay brief-file-overlay--shaded">
                    <div className="brief-file-overlay__content">
                        <div className="heading heading--h5">
                            {Math.floor(100 * this.props.progress)}%
                        </div>
                        <div className="brief-file-overlay__progress-bar">
                            <div className="progress-bar progress-bar--small progress-bar--soft">
                                <div
                                    className="progress-bar__progress"
                                    style={{ width: Math.floor(100 * this.props.progress) + '%' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    }

    renderComment() {
        const { placeholder, comment, onBlur } = this.props;
        return (
            <Textarea
                className="input-textarea"
                placeholder={placeholder}
                value={comment}
                onChange={this.onChange}
                onBlur={onBlur}
                resize="vertical"
                minHeight={100}
            />
        );
    }

    renderActionButton() {
        return (
            <IconButton onClick={this.props.onRemove} variations={['lighter', 'on-white']}>
                <Icon variations={['trash-2']} />
            </IconButton>
        );
    }
}
EditFile.propTypes = {
    placeholder: PropTypes.string,
    progress: PropTypes.number,
    comment: PropTypes.string.isRequired,
    error: PropTypes.bool,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    retryUpload: PropTypes.func,
    file: fileShape.isRequired,
};
EditFile.defaultProps = {
    placeholder: '',
    progress: null,
};

export const ViewFile = ({ file, children }) => {
    const actionButton = (
        <IconButton href={file.url} variations={['lighter', 'on-white']}>
            <Icon variations={['download-1']} />
        </IconButton>
    );
    return <FileContainer comment={children} actionButton={actionButton} file={file} />;
};
ViewFile.propTypes = {
    children: PropTypes.element.isRequired,
    file: fileShape,
};



// WEBPACK FOOTER //
// ./src/web/js/contest/components/brief/file.jsx